import { forwardRef, useImperativeHandle, useRef } from 'react';
import { TextArea } from '@vp/swan';
import type { TextAreaProps } from '@vp/swan';

/**
 * The imperative surface a parent needs: grow the textarea to fit its content, move
 * focus, or reach the underlying element for anything not covered here.
 */
export type ResizableTextAreaRef = {
    focus: () => void;
    blur: () => void;
    resize: () => void;
    baseRef: () => HTMLTextAreaElement | null;
};

export type ResizableTextAreaProps = TextAreaProps;

/**
 * A textarea that grows to fit its content.
 *
 * Height is measured against a hidden shadow textarea holding the placeholder, rather
 * than from `scrollHeight` alone. On iOS Safari a textarea reports a `scrollHeight`
 * below its own single-row height, which collapses the field on the first keystroke;
 * taking the max of the two keeps a stable floor.
 *
 * Resizing is imperative on purpose — the parent owns the value, so it knows when the
 * content changed and can call `resize()` in the same effect. Watching the value here
 * would mean a second source of truth for when to measure.
 */
export const ResizableTextArea = forwardRef<ResizableTextAreaRef, ResizableTextAreaProps>(
    (props, ref) => {
        const textAreaRef = useRef<HTMLTextAreaElement>(null);
        const shadowTextAreaRef = useRef<HTMLTextAreaElement>(null);

        useImperativeHandle(ref, () => ({
            focus: () => textAreaRef.current?.focus(),
            blur: () => textAreaRef.current?.blur(),
            resize: () => {
                const textArea = textAreaRef.current;
                if (!textArea) return;

                const minHeight = shadowTextAreaRef.current?.scrollHeight ?? 0;

                // Reset first: scrollHeight only reports the content height when the
                // element is not already stretched to hold it.
                textArea.style.height = 'auto';
                textArea.style.height = `${Math.max(minHeight, textArea.scrollHeight)}px`;
            },
            baseRef: () => textAreaRef.current,
        }));

        return (
            <>
                <TextArea
                    ref={shadowTextAreaRef}
                    data-testid="shadow-resizable-textarea"
                    rows={props.rows}
                    pt={props.pt}
                    pr={props.pr}
                    pb={props.pb}
                    fullWidth={props.fullWidth}
                    value={props.placeholder}
                    readOnly
                    aria-hidden="true"
                    tabIndex={-1}
                    style={{
                        ...props.style,
                        position: 'absolute',
                        top: '0px',
                        left: '0px',
                        // `visibility` rather than `display: none`: the element must still
                        // be laid out for scrollHeight to mean anything, but must not be
                        // visible or reachable.
                        visibility: 'hidden',
                    }}
                />
                <TextArea ref={textAreaRef} {...props} />
            </>
        );
    }
);

ResizableTextArea.displayName = 'ResizableTextArea';
